import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, Mail } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import LazyImage from './LazyImage';
import { usePerformance } from '../hooks/usePerformance';
import { logger } from '../lib/logger';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating: number;
  reviews: number;
  seller: string;
  supplier_name?: string;
  location: string;
  supplier_location?: string;
  badge?: string;
}

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { measureRender } = usePerformance('ProductCard');
  
  const discount = product.originalPrice 
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;
  
  const { addToCart } = useCart();

  // Use supplier_name if available, fallback to seller, with default value
  const sellerName = product.supplier_name || product.seller || 'Unknown Seller';
  const sellerLocation = product.supplier_location || product.location || 'Unknown Location';

  // Convert seller name to slug for navigation
  const sellerSlug = sellerName.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');

  const handleVisitStore = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to seller profile page
    window.location.href = `/seller/${sellerSlug}`;
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const endRender = measureRender('addToCart');
      
    const cartItem = {
      id: Date.now().toString(),
      productId: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      image: product.image,
      originalPrice: product.originalPrice,
      supplier: {
        id: '1',
        name: sellerName
      }
    };
    
    addToCart(cartItem);
      
      logger.info('Product added to cart', 'ProductCard', { 
        productId: product.id, 
        productName: product.name 
      });
      
      endRender();
    } catch (error) {
      logger.error('Failed to add product to cart', 'ProductCard', { 
        error, 
        productId: product.id 
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 group overflow-hidden">
      <div className="relative">
        <LazyImage 
          src={product.image} 
          alt={product.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          onError={() => logger.warn('Product image failed to load', 'ProductCard', { 
            productId: product.id, 
            imageSrc: product.image 
          })}
        />
        {product.badge && (
          <span className="absolute top-2 left-2 bg-madeinbd-gradient text-white px-2 py-1 text-xs font-semibold rounded">
            {product.badge}
          </span>
        )}
        {discount > 0 && (
          <span className="absolute top-2 right-2 bg-madeinbd-red-500 text-white px-2 py-1 text-xs font-semibold rounded">
            -{discount}%
          </span>
        )}
        <button className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Heart className="h-4 w-4 text-gray-600 hover:text-madeinbd-red-500 transition-colors" />
        </button>
      </div>
      
      <div className="p-4">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-semibold text-gray-900 mb-2 hover:text-madeinbd-600 transition-colors line-clamp-2">
            {product.name}
          </h3>
        </Link>
        
        <div className="flex items-center mb-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                className={`h-4 w-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
              />
            ))}
          </div>
          <span className="text-sm text-gray-600 ml-2">({product.reviews})</span>
        </div>
        
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-madeinbd-600">${product.price}</span>
            {product.originalPrice && (
              <span className="text-sm text-gray-500 line-through">${product.originalPrice}</span>
            )}
          </div>
        </div>
        
        <div className="text-sm text-gray-600 mb-3">
          <button 
            onClick={handleVisitStore}
            className="font-medium hover:text-madeinbd-600 transition-colors"
          >
            {sellerName}
          </button>
          <p className="text-xs">{sellerLocation}</p>
        </div>
        
        <button className="w-full bg-madeinbd-gradient text-white py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center space-x-2">
          <span onClick={handleAddToCart}>Send Inquiry</span>
        </button>
      </div>
    </div>
  );
};

export default ProductCard;